/**
 * Collapse a list of chat rows so each external conversation appears once,
 * preferring the viewer's own seat.
 *
 * Group-based chat visibility (see `apps/api/src/domains/communications/...`
 * visibility filters and the matching dedup-fan-out path in
 * `inbound-message`) gives every group member their own seat row for the
 * same external conversation. The unique fan-out trigger is a non-null
 * `group_id`: outside a group each chat row is already unique by id, so
 * no dedup is needed there.
 *
 * For grouped rows, the conversation key is whatever uniquely identifies
 * the *external* conversation (WhatsApp `jid`, SMS `phone_number`). When
 * multiple seats share that key within the same group, we keep one —
 * preferring the viewer's row so any id-keyed lookup downstream
 * (mark-as-read, archive, send-reply) resolves against the row the viewer
 * actually owns. Mirrors `dedupeEmailsByMessageId`.
 */
export function dedupeChatsByConversationKey<
  T extends {
    id: string;
    user_id?: string | null;
    group_id?: string | null;
  },
>(
  chats: T[],
  conversationKey: (chat: T) => string | null | undefined,
  viewerUserId: string | undefined,
): T[] {
  const byKey = new Map<string, T>();
  for (const chat of chats) {
    const convKey = conversationKey(chat);
    // Outside a group there's no fan-out, so keep the row by its unique id.
    const key =
      chat.group_id && convKey ? `${chat.group_id}::${convKey}` : chat.id;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, chat);
      continue;
    }
    if (
      viewerUserId &&
      chat.user_id === viewerUserId &&
      existing.user_id !== viewerUserId
    ) {
      byKey.set(key, chat);
    }
  }
  return [...byKey.values()];
}
